import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { cn } from "@omni/utils/styles";
import { ReactNode, useEffect, useState } from "react";

import { getDeliveryType } from "@/redux/modules/value/productDetail/productDetail.module";
import { getTagZoneIdFor } from "@/utils/tagging/tag.utils";

import { useECommerceContext } from "../../context/eCommerce.context";
import { useStorePickerModalContext } from "../../context/storePickerModal.context";
import { useECommerceProduct } from "../../hooks/useECommerceProduct.hook";
import { useProductDetail } from "../../hooks/useProductDetail.hook";
import { DeliveryContextProvider } from "./context/delivery.context";

type Props = {
    className?: string;
    children: ReactNode;
};

const DeliveryBase = ({ className, children }: Props) => {
    const {
        sku,
        visibilityType,
        minimumOrderQuantity,
        roundingValue,
        isLimitedStock,
        isBoxArticle,
        isBoxSoldOut,
        deliveryTypes,
        deliveryOptions,
        montageParents,
        montages,
        boxLogisticInformation
    } = useECommerceProduct();

    const { setStorePickerModalState } = useStorePickerModalContext();

    const { updateProductDetail, deliveryType, quantity, serviceProductSkus } =
        useProductDetail();

    const { xfPath, tooltips, infoLink, isEcommerceReady, setInitialised } =
        useECommerceContext();

    const [deliveryTypesDisabled, setDeliveryTypesDisabled] = useState(true);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        const deliveryTypeToSet = getDeliveryType();
        let quantityToSet = Math.max(quantity || 0, 1);

        if (deliveryTypeToSet !== DeliveryType.ClickAndCollect) {
            quantityToSet = minimumOrderQuantity;
        }

        updateProductDetail({
            deliveryType: deliveryTypeToSet,
            quantity: quantityToSet,
            sku: sku
        }).then(() => {
            setDeliveryTypesDisabled(false);
            setInitialised(true);
        });
    }, []);

    useEffect(() => {
        if (deliveryType !== DeliveryType.HomeDelivery) return;

        const currentQuantity = quantity || 0;
        const newQuantity = Math.max(currentQuantity, minimumOrderQuantity);

        if (currentQuantity !== newQuantity) {
            updateProductDetail({ quantity: newQuantity });
        }
    }, [deliveryType, minimumOrderQuantity, quantity, updateProductDetail]);

    return (
        <DeliveryContextProvider
            sku={sku}
            onProductDetailChange={updateProductDetail}
            quantity={quantity}
            serviceProductSkus={serviceProductSkus}
            deliveryType={deliveryType}
            visibilityType={visibilityType}
            deliveryOptions={deliveryOptions}
            minimumOrderQuantity={minimumOrderQuantity}
            roundingValue={roundingValue}
            xfPath={xfPath}
            tooltips={tooltips}
            isLimitedStock={isLimitedStock}
            infoLink={infoLink}
            deliveryTypesDisabled={deliveryTypesDisabled}
            deliveryTypes={deliveryTypes}
            isBoxArticle={isBoxArticle}
            montageParents={montageParents}
            montages={montages}
            boxLogisticInformation={boxLogisticInformation}
            isBoxSoldOut={isBoxSoldOut}
            setStorePickerModalState={setStorePickerModalState}
            isUpdatingCart={!isEcommerceReady}
        >
            <div
                className={cn(className)}
                {...getTagZoneIdFor("pdp-delivery")}
            >
                {children}
            </div>
        </DeliveryContextProvider>
    );
};

export { DeliveryBase };
